<template>
  <div class="create-credentials">
    <progress-header
      :top-image-path="'header-cc-square-top.svg'"
      :bottom-image-path="'header-bank-square-bottom.svg'"
      :header-text="'המערכת זיהתה בהצלחה את חיובי האשראי שלך'"
      :color-variant="'blue'"
    />
    <div class="add-credit-card-description">
      <div class="title">חיבור חשבונות אשראי</div>
      <div class="subtitle">כדי להציג במדויק איפה ועל מה יוצא הכסף שלך כל חודש, המערכת תצטרך גישה לקריאה בלבד מחשבונות האשראי שלך.</div>
    </div>
    <div class="tool-tip-wrapper">
      <base-tool-tip class="info-tool-tip" :icon-path="'icon-info.svg'">
        <div class="info-text">
          יכול להיות שחלק מהחיובים ברשימה הם <span class="bold">הלוואות</span> ולא חיובי כרטיס אשראי. מאלו אפשר להתעלם כרגע.
        </div>
      </base-tool-tip>
    </div>
    <separator/>
    <div class="credit-card-section">
      <div class="credit-card-section-content" v-for="(creditCardQuestions, account) in questionsByAccount" :key="account">
        <div class="account">חשבון מס׳ {{ account }}</div>
        <div v-for="(question, index) in creditCardQuestions" :key="index">
          <credit-card-card
            class="credit-card-wrapper"
            :is-connected="question.connected"
            :source="question.source"
            :connect-credit-card="() => onSaveCardCredentialsClicked(question.source.sourceName)"
            :billing-amount="question.billingAmount"
            :billing-date="question.billingDate"
            :business-name="question.businessName"/>
        </div>
      </div>
    </div>
    <div class="button-wrapper">
      <riseup-button class="next-button" :title="'אפשר להמשיך'" :action="checkAndContinue" :size="'small'"/>
    </div>
  </div>
</template>

<script>
import BaseUI from 'base-ui';
import _ from 'lodash';
import { mapState, mapGetters, mapActions } from 'vuex';
import Segment from '../../../../../../Segment';
import ClosedCard from '../../../../../../base-components/ClosedCard.vue';
import MultiStateCredentialsCard from '../../../../../../base-components/credentials/credentials-card/MultiStateCredentialsCard.vue';
import ShowUnsupportedModalMixin from '../../../unsupported-banks/ShowUnsupportedModalMixin.vue';
import Loader from '../../../../../../base-components/Loader.vue';
import ProgressHeader from '../../../../../../base-components/onboarding/ProgressHeader.vue';
import CreditCardCard from '../../../../../../base-components/onboarding/CreditCardCard.vue';
import BaseToolTip from '../../../../../../base-components/onboarding/BaseToolTip.vue';
import NoCreditCardsContinuePopup from '../../../../../../base-components/onboarding/NoCreditCardsContinuePopup.vue';
import ModalBus from '../../../../../../event-bus/modal-bus';

export default {
  name: 'CreditCardsCredentialsOnboarding',
  mixins: [ShowUnsupportedModalMixin],
  async created() {
    this.startLoading(this.$options.name);
    await this.fetchCreditCardsQuestions();
    this.doneLoading(this.$options.name);
    Segment.trackUserGot('CreditCardsConnectionEntered', { numOfCards: this.creditCardQuestions.length });
    if (_.isEmpty(this.creditCardQuestions)) {
      await this.nextPage();
    }
    window.scrollTo(0, 0);
  },
  props: {
    onConnection: {
      type: Function,
      required: true,
    },
  },
  components: {
    MultiStateCredentialsCard,
    ClosedCard,
    Loader,
    ProgressHeader,
    CreditCardCard,
    BaseToolTip,
    Separator: BaseUI.Separator,
    RiseupButton: BaseUI.RiseupButton,
    NoCreditCardsContinuePopup,
  },
  computed: {
    ...mapState('creditCardQuestions', ['creditCardQuestions']),
    ...mapGetters('creditCardQuestions', ['questionsByAccount']),
  },
  methods: {
    ...mapActions('creditCardQuestions', ['fetchCreditCardsQuestions']),
    ...mapActions('onboardingState', ['nextPage']),
    ...mapActions('loader', ['startLoading', 'doneLoading']),

    async onSaveCardCredentialsClicked(sourceName) {
      Segment.trackUserInteraction('ConnectCreditCardClicked', { sourceName });
      this.onConnection(sourceName);
    },

    async onNextPage() {
      const numOfConnectedCards = this.numOfConnectedCards();
      Segment.trackUserInteraction('CreditCardsConnectionNextClicked', { numOfConnectedCards });
      await this.nextPage();
    },

    checkAndContinue() {
      if (this.numOfConnectedCards() === 0) {
        ModalBus.$emit('open', {
          component: NoCreditCardsContinuePopup,
          props: { nextPage: this.onNextPage },
        });
      } else {
        this.onNextPage();
      }
    },

    numOfConnectedCards() {
      return _.filter(this.creditCardQuestions, q => q.connected).length;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../scss/mixins';

  .create-credentials {
    display: flex;
    flex-direction: column;
    align-items: center;

    .add-credit-card-description {
      @include responsive-padding;
      margin-top: 40px;

      .title {
        font-size: 18px;
        font-weight: bold;
        color: $riseup_v2_blue;
        margin-bottom: 20px;
      }

      .subtitle {
        font-size: 18px;
        color: $riseup_black;
        margin-bottom: 20px;
        line-height: 1.44;
      }
    }

    .tool-tip-wrapper {
      @include responsive-padding;
      margin-bottom: 40px;

      .info-tool-tip {
        width: 100%;

        .info-text {
          font-family: SimplerPro;
          font-size: 14px;
          line-height: 1.36;
          height: 100%;

          .bold {
            font-weight: bold
          }
        }
      }
    }

    .credit-card-section {
      width: 100%;
      background-color: $riseup_v2_light_grey;

      .credit-card-section-content {
        @include responsive-padding;

        .account {
          font-size: 14px;
          font-weight: bold;
          color: $riseup_black;
          margin: 25px 0 30px;
        }

        .credit-card-wrapper {
          margin-bottom: 16px;
        }
      }
    }

    .button-wrapper {
      @include responsive-padding;
      @include responsive-padding-top-bottom;
      width: 100%;

      .next-button {
        width: 100%;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/pages/responsive-pages/authenticated/onboard2/steps/credit-card-connection/CreditCardCredentialsOnboarding.vue